import { DocumentStatus } from '@kanbu/schema/enums';
import { useLingui } from '@lingui/react/macro';
import { Badge } from '@utima/ui';

export interface DocumentProcessingStatusProps {
  status: string;
  label: string;
  pdfProgress?: { completedPages: number; totalPages: number } | null;
}

export function DocumentProcessingStatus({
  status,
  label,
  pdfProgress,
}: DocumentProcessingStatusProps) {
  const { t } = useLingui();
  const hasProgress =
    !!pdfProgress &&
    Number.isSafeInteger(pdfProgress.totalPages) &&
    pdfProgress.totalPages > 0 &&
    Number.isSafeInteger(pdfProgress.completedPages) &&
    pdfProgress.completedPages >= 0;
  const reading =
    status === DocumentStatus.Parsing ||
    status === DocumentStatus.ParsingAsync ||
    (status === DocumentStatus.Ready && hasProgress);
  const indexing =
    status === DocumentStatus.Parsed || status === DocumentStatus.Embedding;
  const active = reading || indexing;
  const completed = hasProgress
    ? Math.min(pdfProgress.completedPages, pdfProgress.totalPages)
    : 0;
  const total = hasProgress ? pdfProgress.totalPages : 0;
  const variant =
    status === DocumentStatus.ParsingError ||
    status === DocumentStatus.EmbeddingError
      ? 'danger'
      : status === DocumentStatus.Done
        ? 'success'
        : (status === DocumentStatus.Ready && !hasProgress) ||
            status === DocumentStatus.Uploading
          ? 'warning'
          : 'info';

  return (
    <div className='flex w-fit flex-col items-start gap-1.5 py-1'>
      <Badge
        size='sm'
        variant={variant}
        outline
        title={indexing ? t`Preparing for search` : undefined}
      >
        {active && (
          <span
            aria-hidden
            className='mr-1.5 inline-block size-1.5 shrink-0 rounded-full bg-current motion-safe:animate-pulse'
          />
        )}
        {indexing ? t`Indexing` : reading ? t`Processing` : label}
      </Badge>
      {hasProgress && active && (
        <span className='pl-0.5 text-xs tabular-nums text-muted-fg'>
          {t`${completed} / ${total} pages`}
        </span>
      )}
    </div>
  );
}
